import { decodeTime, monotonicFactory, ulid } from 'ulid';
import { z } from 'zod';

/**
 * Primary key generation.
 *
 * Boilerplate plan §3.D.2. Every primary key in the schema is an
 * application-generated ULID stored as `char(26)` — see `db/CONVENTIONS.md`.
 * The database never generates an id, so this is the only place one is made.
 *
 * ULIDs are lexicographically sortable by creation time, which is why the
 * ordering rule in §4.2 can fall back to `ORDER BY position, id` and get a
 * stable, meaningful tiebreak for free.
 */

/** Crockford base32: the alphabet excludes I, L, O and U to stay unambiguous. */
const ULID_PATTERN = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;

export const ULID_LENGTH = 26;

/**
 * Monotonic, not the plain generator.
 *
 * A bare `ulid()` draws fresh randomness on every call, so two ids created in
 * the same millisecond sort in arbitrary order relative to each other. That
 * breaks the property this module is relied on for the moment anything creates
 * rows in a burst — pasting many cards, a seed fixture, a bulk move — and it
 * breaks it invisibly, because the ids still look right. The monotonic factory
 * increments the random component instead when the timestamp has not advanced.
 *
 * The guarantee is per-process. Two servers creating ids in the same
 * millisecond still interleave arbitrarily; nothing in the schema depends on
 * cross-process ordering, and `ORDER BY position, id` only needs the tiebreak
 * to be *stable*, not globally chronological.
 */
const nextUlid = monotonicFactory();

/** Generate a new primary key. */
export function newId(): string {
  return nextUlid();
}

/**
 * A ULID whose time component is exactly `ms` — for fixtures that backdate a
 * row and need its id to agree with the backdated `created_at`
 * (`docs/notes/batch-reads.md`, "Test plan").
 *
 * ⚠ **The plain `ulid(ms)`, never `nextUlid`.** A monotonic factory fed a seed
 * earlier than the last time it saw keeps that LAST timestamp and increments
 * the random part, so a backdated id minted after any `newId()` would silently
 * carry the current time — and every test that relies on the id's time (the
 * absent-anchor fallback of `GET /cards/:id/comments?after=`) would pass or
 * fail for the wrong reason. Ordering inside one millisecond is therefore not
 * monotonic here; a caller that needs it sorts by id after minting.
 */
export function newIdAt(ms: number): string {
  return ulid(ms);
}

/**
 * The instant a ULID encodes, in epoch milliseconds — `ulid`'s `decodeTime`,
 * re-exported here so that only `@molt/shared` depends on `ulid`.
 *
 * Used by `GET /cards/:id/comments?after=` when the anchor comment is gone:
 * the id's time is the fallback boundary. It is an UPPER bound on the row's
 * `created_at`, never the value itself — every writer mints the id inside its
 * transaction, after `BEGIN`, and `created_at` is the transaction's start — so
 * the caller subtracts a margin rather than trusting it. Throws on a string
 * that is not a ULID; callers pass a value `ulidSchema` already accepted.
 */
export function ulidTimeMs(id: string): number {
  return decodeTime(id);
}

/**
 * A ULID as the schema stores it.
 *
 * Case matters. Postgres compares text case-sensitively under any deterministic
 * collation, so `01J...` and `01j...` are different rows — the parser rejects
 * lowercase rather than quietly folding it, because folding here is how two
 * distinct ids become one.
 */
export const ulidSchema = z
  .string()
  .length(ULID_LENGTH)
  .regex(ULID_PATTERN, 'must be a Crockford base32 ULID (uppercase)');

export function isUlid(value: string): boolean {
  return ULID_PATTERN.test(value);
}
